import { MutationTree } from 'vuex';
import { ErrorDetail } from '../../types';
import { LicenseState, License, CapacityInfo, ActiveInfo, NewLicenseResult, LicenseDetails } from './types';
import * as types from './mutation-types';

// エラーをクリアする
const resetErrors = (state: LicenseState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorTitle = undefined;
    searchState.errorDetails = undefined;

    const createState = state.create;
    createState.error = false;
    createState.errorTitle = undefined;
    createState.errorDetails = undefined;

    const activateState = state.activate;
    activateState.error = false;
    activateState.errorTitle = undefined;
    activateState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const capacityState = state.capacity;
    capacityState.error = false;
    capacityState.errorCode = undefined;
    capacityState.errorMsg = undefined;

    const activeState = state.active;
    activeState.error = false;
    activeState.errorCode = undefined;
    activeState.errorMsg = undefined;

    const destroyState = state.destroy;
    destroyState.error = false;
    destroyState.errorTitle = undefined;
    destroyState.errorDetails = undefined;
};
export const mutations: MutationTree<LicenseState> = {
    // 検索系
    [types.SEARCH_START](state) {
        resetErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; licenses: Array<License> }) {
        state.count = payload.count;
        state.licenses = payload.licenses;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const searchState = state.search;
        searchState.errorTitle = payload.errorTitle;
        searchState.errorDetails = payload.errorDetails;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // 登録系
    [types.CLEAR_CREATE_ERROR](state) {
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isCreated = false;
    },

    [types.CREATE_START](state) {
        resetErrors(state);
        const createState = state.create;
        createState.error = false;
        createState.errorTitle = undefined;
        createState.errorDetails = undefined;
        createState.isProcessing = true;
    },

    [types.CREATE_SUCCESS](state, payload: NewLicenseResult) {
        state.createResult = payload;

        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = true;
    },

    [types.CREATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const createState = state.create;
        createState.errorTitle = payload.errorTitle;
        createState.errorDetails = payload.errorDetails;
        createState.error = true;
        createState.isProcessing = false;
    },

    // アクティベーション処理系
    [types.CLEAR_ACTIVATE_ERROR](state) {
        const activateState = state.activate;
        activateState.error = false;
        activateState.errorTitle = undefined;
        activateState.errorDetails = undefined;
    },

    [types.ACTIVATE_START](state) {
        resetErrors(state);
        const activateState = state.activate;
        activateState.error = false;
        activateState.errorTitle = undefined;
        activateState.errorDetails = undefined;
        activateState.isProcessing = true;
    },

    [types.ACTIVATE_SUCCESS](state) {
        const activateState = state.activate;
        activateState.isProcessing = false;
    },

    [types.ACTIVATE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const activateState = state.activate;
        activateState.errorTitle = payload.errorTitle;
        activateState.errorDetails = payload.errorDetails;
        activateState.error = true;
        activateState.isProcessing = false;
    },

    // 詳細情報参照系
    [types.SHOW_START](state) {
        resetErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: LicenseDetails) {
        state.details = payload;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // 詳細情報参照系
    [types.CAPACITY_START](state) {
        resetErrors(state);
        const capacityState = state.capacity;
        capacityState.isProcessing = true;
        capacityState.isFetchSuccess = false;
    },

    [types.CAPACITY_SUCCESS](state, payload: CapacityInfo) {
        state.capacityInfo = payload;

        const capacityState = state.capacity;
        capacityState.isProcessing = false;
        capacityState.isFetchSuccess = true;
    },

    [types.CAPACITY_FAILURE](state, payload: { code: string; message: string }) {
        const capacityState = state.capacity;
        capacityState.errorCode = payload.code;
        capacityState.errorMsg = payload.message;
        capacityState.error = true;
        capacityState.isProcessing = false;
        capacityState.isFetchSuccess = false;
    },

    // 適応ライセンス情報取得系
    [types.ACTIVE_START](state) {
        resetErrors(state);
        const activeState = state.active;
        activeState.isProcessing = true;
        activeState.isFetchSuccess = false;
    },

    [types.ACTIVE_SUCCESS](state, payload: ActiveInfo) {
        state.active.activeInfo = payload;

        const activeState = state.active;
        activeState.isProcessing = false;
        activeState.isFetchSuccess = true;
    },

    [types.ACTIVE_FAILURE](state, payload: { code: string; message: string }) {
        const activeState = state.active;
        activeState.errorCode = payload.code;
        activeState.errorMsg = payload.message;
        activeState.error = true;
        activeState.isProcessing = false;
        activeState.isFetchSuccess = false;
    },

    [types.DESTROY_START](state) {
        resetErrors(state);
        const destroyState = state.destroy;
        destroyState.isProcessing = true;
    },

    [types.DESTROY_SUCCESS](state) {
        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.DESTROY_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const destroyState = state.destroy;
        destroyState.errorTitle = payload.errorTitle;
        destroyState.errorDetails = payload.errorDetails;
        destroyState.error = true;
        destroyState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const createState = state.create;
        createState.isProcessing = false;
        createState.isCreated = false;

        const activateState = state.activate;
        activateState.isProcessing = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const capacityState = state.capacity;
        capacityState.isProcessing = false;
        capacityState.isFetchSuccess = false;

        const activeState = state.active;
        activeState.isProcessing = false;
        activeState.isFetchSuccess = false;

        const destroyState = state.destroy;
        destroyState.isProcessing = false;
    },

    [types.CLEAR_ALL](state) {
        // activeInfo(適応ライセンス情報)は消さないこと
        resetErrors(state);
        state.licenses = undefined;
        state.count = 0;
        state.details = undefined;
        state.capacityInfo = undefined;
        state.createResult = undefined;
    },
};
